import React, {
  forwardRef,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes } from "utils/formUtils";
import message from "components/CustomMessage";
import GenericMappingForm from "../GenericMappingForm";
import { isEmpty, uniqBy, map } from "lodash";
import { Form } from "antd";

export const GITLAB_SECURITY_SCAN_TYPE_OPTIONS = {
  SAST: "SAST",
  "Secret Detection": "SECRET_DETECTION",
  "Container Security Tool": "CONTAINER_SCANNING",
  IaC: "IaC",
  "Dependency scanning": "DEPENDENCY_SCANNING",
  DAST: "DAST",
  "API Fuzzing": "API_FUZZING"
};

const GitlabSecurityMappingForm = forwardRef(function GitlabSecurityMappingForm(
  props,
  ref
) {
  const [mappedProjectList] = useState(() => {
    if (props.record) {
      const { projectId, projectName } = props.record;
      const projectIdArray = projectId.split(",");
      const projectNameArray = projectName.split(",");
      return {
        projects: projectIdArray.map((id, index) => ({
          id,
          name: projectNameArray[index],
        })),
      };
    }
    return { projects: [] };
  });

  const projectRef = useRef({ projects: mappedProjectList.projects });
  const apiParamsRef = useRef({});          //send name param to api similar to what we did in SDElementsMapform
  const [form] = Form.useForm();

  const fetchProjects = useCallback((params) => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        login_id: props.loginConfigId,
        ...params
      });
    promise
      .then((response) => {
        let { projects } = response;
        if (mappedProjectList["projects"].length > 0 && !isEmpty(projects)) {
          response.projects = uniqBy(
            [...projects, ...mappedProjectList["projects"]],
            "id"
          );
        }

        projectRef.current["projects"] = response.projects;
      })
      .catch(() => {
        message.error("Problem fetching projects");
      });

    return { promise, cancel };
  }, [props.loginConfigId, props.toolName, mappedProjectList]);

  const getExtraFieldsData = (config) => {
    const { projectId, scanType } = config;
    const data = projectRef.current.projects.reduce(
      (acc, cv) => {
        const { id, name } = cv;
        if (projectId.includes(id)) {
          acc.id.push(id);
          acc.name.push(name);
        }
        return acc;
      },
      { id: [], name: [] }
    );

    return {
      projectId: data.id.join(),
      projectName: data.name.join(),
      scanType: !isEmpty(scanType)
        ? map(scanType, (i) => GITLAB_SECURITY_SCAN_TYPE_OPTIONS[i]).join()
        : undefined,
    };
  };

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { projectId, projectName, scanType } = config;
    return {
      projectId: projectId.split(","),
      projectName: projectName.split(","),
      scanType: !isEmpty(scanType)
        ? scanType
            .split(",")
            .map((type) =>
              Object.keys(GITLAB_SECURITY_SCAN_TYPE_OPTIONS).find(
                (i) => GITLAB_SECURITY_SCAN_TYPE_OPTIONS[i] === type
              )
            )
        : undefined,
    };
  }, []);

  const extraFields = useMemo(
    () => [
      {
        name: "projectId",
        colSpan: "12",
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        label: "Project",
        required: true,
        props: {
          mode: "multiple",
          autoFocus: true,
          api: fetchProjects,
          setExtraProps: (fieldProps, currentField) => {
            const configurations = form.getFieldValue("configurations");
            const currentConfiguration = configurations[currentField.name] || {};
            const apiParams = apiParamsRef.current;
            const keyName = currentConfiguration.projectId;
            if (!apiParams["name"]) {
              apiParams["name"] = keyName;
            }
            return {
              resetOptionsProp: apiParams[keyName],
              apiParams: apiParams[keyName],
            }
          },
        },
      },
      {
        name: "scanType",
        colSpan: 12,
        type: FieldTypes.MULTI_SELECT,
        label: "Scan Type",
        options: Object.keys(GITLAB_SECURITY_SCAN_TYPE_OPTIONS),
      },
    ],
    [fetchProjects, form]
  );

  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      extraFields={extraFields}
      {...props}
    />
  );
});

export default GitlabSecurityMappingForm;
